import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const SelfEsteemBuilding = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [symptomsCanScrollLeft, setSymptomsCanScrollLeft] = useState(false);
  const [symptomsCanScrollRight, setSymptomsCanScrollRight] = useState(true);
  const [causesCanScrollLeft, setCausesCanScrollLeft] = useState(false);
  const [causesCanScrollRight, setCausesCanScrollRight] = useState(true);
  const [typesCanScrollLeft, setTypesCanScrollLeft] = useState(false);
  const [typesCanScrollRight, setTypesCanScrollRight] = useState(true);
  const symptomsScrollRef = useRef(null);
  const causesScrollRef = useRef(null);
  const typesScrollRef = useRef(null);
  const treatmentScrollRef = useRef(null);

  // Scroll detection for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'symptoms', 'causes', 'types', 'treatment'];
      const scrollPosition = window.scrollY + 250; // Offset for better detection

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filterContent = (section) => {
    setActiveSection(section);
  };

  const checkScrollPosition = (ref) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      const canScrollLeft = scrollLeft > 0;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
      return { canScrollLeft, canScrollRight };
    }
    return { canScrollLeft: false, canScrollRight: false };
  };

  const scrollSymptoms = (direction) => {
    if (symptomsScrollRef.current) {
      const scrollAmount = 320; // Card width + gap
      symptomsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(symptomsScrollRef);
        setSymptomsCanScrollLeft(position.canScrollLeft);
        setSymptomsCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollCauses = (direction) => {
    if (causesScrollRef.current) {
      const scrollAmount = 320;
      causesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(causesScrollRef);
        setCausesCanScrollLeft(position.canScrollLeft);
        setCausesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollTypes = (direction) => {
    if (typesScrollRef.current) {
      const scrollAmount = 320;
      typesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(typesScrollRef);
        setTypesCanScrollLeft(position.canScrollLeft);
        setTypesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollTreatment = (direction) => {
    if (treatmentScrollRef.current) {
      const scrollAmount = 320;
      treatmentScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#E6F7F4]"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
          <div className="relative overflow-hidden rounded-3xl bg-[#E6F7F4]">
            <div className="relative px-6 py-24 sm:px-10 sm:py-32 lg:px-12 lg:py-36 text-center">
              <img
                src="/static/img/services/self-confidence.png"
                alt=""
                className="pointer-events-none absolute -right-4 top-1/2 hidden w-80 -translate-y-1/2 opacity-75 lg:block"
              />

              <div className="mx-auto flex items-center justify-center gap-2 text-xs tracking-wide text-gray-700">
                <span>Adult Services</span>
              </div>

              <h1 className="mt-6 font-extrabold tracking-tight text-gray-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
                Self-Esteem Building
              </h1>

              <p className="mt-6 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Build confidence and self-worth through personalized therapy. Evidence-based techniques, positive psychology, and empowering strategies to help you recognize your true value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Action Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center py-4">
            <div className="flex flex-wrap items-center gap-6">
              <a 
                href="#overview" 
                className={`font-medium transition-colors ${
                  activeSection === 'overview' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('overview');
                  const offset = 200; // Height of main navbar + action bar + extra spacing + padding
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Overview
              </a>
              <a 
                href="#symptoms" 
                className={`font-medium transition-colors ${
                  activeSection === 'symptoms' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('symptoms');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Symptoms
              </a>
              <a 
                href="#causes" 
                className={`font-medium transition-colors ${
                  activeSection === 'causes' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('causes');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Causes and Risk
              </a>
              <a 
                href="#types" 
                className={`font-medium transition-colors ${
                  activeSection === 'types' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('types');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Types
              </a>
              <a 
                href="#treatment" 
                className={`font-medium transition-colors ${
                  activeSection === 'treatment' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('treatment');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Treatment
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Overview */}
          <div id="overview" className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Overview</h2>
            <p className="text-lg text-gray-600 mb-8">
              Self-esteem building involves developing a positive self-image and confidence in one’s own abilities and worth. 
              This form of therapy helps clients recognise and appreciate their own positive attributes and overcome negative self-perceptions.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Individuals suffering from low self-esteem may experience several mental health problems, and it is through a series of techniques and interventions
               that they learn to love and appreciate themselves, helping them to develop the confidence necessary for a fulfilling relationship.
            </p>
          </div>
        </div>

        {/* Symptoms Section with Full-Width Background */}
        <div id="symptoms" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Symptoms </h2>
            
            {/* Common Signs */}
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Recognizing signs of low self-esteem</h3>
              
              {/* Scroll Container */}
              <div 
                ref={symptomsScrollRef}
                className="overflow-x-auto scrollbar-hide mb-4"
              >
                <div className="flex space-x-4 py-4 w-fit">
                  {[
                    {
                      symptom: "Low Confidence In One's Abilities",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Placing The Blame On Others For The Outcome",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Comparing Oneself Unfavorably To Others",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Difficulty In Seeking Help",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Constant Worrying And Self-Doubting",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Rejection Of Praise And Compliments",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Negative Self-Talk",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Fear Of Making Mistakes",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Pessimistic Attitude Toward The Future",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Poor Personal Boundaries",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Tendency To Try To Please Everyone",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      )
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-[300px]">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">{item.symptom}</h4>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-center items-center space-x-4">
                <button
                  onClick={() => scrollSymptoms('left')}
                  disabled={!symptomsCanScrollLeft}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollLeft 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => scrollSymptoms('right')}
                  disabled={!symptomsCanScrollRight}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollRight 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hide Scrollbar Styles */}
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

        {/* Causes Section with Full-Width Background */}
        <div id="causes" className="bg-gray-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Causes and Risk Factors</h2>
            
            {/* Possible Causes */}
            <div>
              <h3 className="text-lg font-normal text-gray-700 mb-6">Understanding factors affecting self-esteem</h3>
              
              {/* Grid Container - No Scrolling */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: "Childhood Experiences",
                    description: "Unsupportive parenting, lack of affection, trauma, abuse, or overcritical teachers.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Social Factors",
                    description: "Bullying, social rejection, discrimination, or pressure from societal standards.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Ongoing Life Events",
                    description: "Financial strain, chronic stress, relationship failures, or job loss.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    )
                  },
                  {
                    title: "Internal Factors",
                    description: "Perfectionism, negative self-talk, and unrealistic comparisons to others.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  }
                ].map((cause, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {cause.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{cause.title}</h4>
                        <p className="text-sm text-gray-600">{cause.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      {/* Types Section with Full-Width Background */}
      <div id="types" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Types of Self-Esteem Issues</h2>
          <h3 className="text-lg font-normal text-gray-600 mb-6">Different patterns of self-esteem challenges</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Low Self-Esteem",
                description: "Feelings of inadequacy, worthlessness, and intense self-doubt with negative self-talk and social anxiety.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              },
              {
                title: "Fragile Self-Esteem",
                description: "Self-worth fluctuates with circumstances and depends heavily on external feedback.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )
              },
              {
                title: "Inflated Self-Esteem",
                description: "Unrealistic, arrogant view of abilities with tendency to look down on others and inability to accept criticism.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              },
              {
                title: "Contingent Self-Esteem",
                description: "Self-esteem dependent on achieving specific goals or meeting certain standards like performance or appearance.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                )
              },
              {
                title: "Implicit Misalignment",
                description: "Conscious self-esteem doesn't match unconscious feelings of self-worth.",
                icon: (
                  <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )
              }
            ].map((item, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">{item.icon}</div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

        {/* Treatment Section with Full-Width Background */}
        <div id="treatment" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Treatments</h2>
            
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Effective approaches for building healthy self-esteem</h3>
              
              {/* Grid Container - No Scrolling */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: "Cognitive Behavioral Therapy",
                    description: "Challenging negative thoughts and developing positive self-talk patterns",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    )
                  },
                  {
                    title: "Strength-Based Therapy",
                    description: "Focusing on personal strengths and achievements to build confidence",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Assertiveness Training",
                    description: "Learning to express needs and set healthy boundaries confidently",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )
                  },
                  {
                    title: "Mindfulness and Self-Compassion",
                    description: "Developing kindness toward oneself and present-moment awareness",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )
                  }
                ].map((treatment, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {treatment.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{treatment.title}</h4>
                        <p className="text-sm text-gray-600">{treatment.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SelfEsteemBuilding;
